import React, { useState } from "react";
import FilterBar from "components/FilterBar";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSChip from "../../system-design/DSChip";
import DSTableCell from "../../system-design/Table/DSTableCell";
import DSBaseTable, {
  Column,
} from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { Refresh, Clear } from "@mui/icons-material";
import usePersonalFeedHistory from "../hooks/usePersonalFeedHistory";
import { PersonalFeedHistory } from "../../../api/personalFeed";
import CalendarEventDialog from "./CalendarEventDialog";
import { DateTime } from "../../../api/calendarEvent";

const FeedHistoryTab = () => {
  const [selectedFeedItem, setSelectedFeedItem] =
    useState<PersonalFeedHistory | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [dateInput, setDateInput] = useState<DateTime | undefined>(undefined);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = usePersonalFeedHistory({ limit: 20, date: dateInput });

  const handleRefresh = () => {
    refetch();
  };

  const handleClearDate = () => {
    setDateInput(undefined);
  };

  const handleChangeDateInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (value) {
      const dateParts = value.split("-");
      const date: DateTime = {
        year: parseInt(dateParts[0], 10),
        month: parseInt(dateParts[1], 10),
        day: parseInt(dateParts[2], 10),
      };
      setDateInput(date);
    } else {
      setDateInput(undefined);
    }
  };

  const handleItemClick = (feedItem: PersonalFeedHistory) => {
    setSelectedFeedItem(feedItem);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedFeedItem(null);
  };

  const columns: Column<PersonalFeedHistory>[] = [
    {
      field: "date",
      label: "Date",
      size: 120,
    },
    {
      field: "name",
      label: "Event Name",
      size: 300,
    },
    {
      field: "country",
      label: "Country",
      size: 150,
    },
    {
      field: "states",
      label: "States",
      size: 200,
    },
    {
      field: "displayedKlydos",
      label: "Klydos",
      size: 100,
    },
  ];

  const rowBuilder = (feedItem: PersonalFeedHistory) => {
    const formattedDate = `${feedItem.date.day}/${feedItem.date.month}/${feedItem.date.year}`;

    return (
      <>
        <DSTableCell sx={{ width: 120 }}>
          <DSTypography variant="body2" fontWeight="medium">
            {formattedDate}
          </DSTypography>
        </DSTableCell>
        <DSTableCell sx={{ width: 300 }}>
          <DSTypography
            variant="body2"
            fontWeight="bold"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "280px",
            }}
          >
            {feedItem.name}
          </DSTypography>
        </DSTableCell>
        <DSTableCell sx={{ width: 150 }}>
          <DSChip
            label={feedItem.country?.name}
            size="small"
            color="primary"
            variant="outlined"
          />
        </DSTableCell>
        <DSTableCell sx={{ width: 200 }}>
          <DSBox sx={{ maxWidth: "180px" }}>
            {feedItem.states && feedItem.states.length > 0 ? (
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {feedItem.states.slice(0, 2).map((state, idx) => (
                  <DSChip
                    key={idx}
                    label={state.name}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))}
                {feedItem.states.length > 2 && (
                  <DSTypography variant="caption" color="text.secondary">
                    +{feedItem.states.length - 2} more
                  </DSTypography>
                )}
              </DSBox>
            ) : (
              <DSTypography variant="body2" color="text.secondary">
                All
              </DSTypography>
            )}
          </DSBox>
        </DSTableCell>
        <DSTableCell sx={{ width: 100 }}>
          <DSChip
            label={feedItem.displayedKlydos.length.toString()}
            size="small"
            color="info"
            variant="filled"
          />
        </DSTableCell>
      </>
    );
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox sx={{ textAlign: "center", py: 4 }}>
        <DSTypography color="error" gutterBottom>
          Error loading feed history: {error.message}
        </DSTypography>
        <DSSecondaryButton onClick={handleRefresh}>Try Again</DSSecondaryButton>
      </DSBox>
    );
  }

  return (
    <DSBox>
      <FilterBar
        activeCount={dateInput ? 1 : 0}
        quickActions={
          <DSSecondaryButton onClick={handleRefresh} startIcon={<Refresh />}>
            Refresh
          </DSSecondaryButton>
        }
      >
        <DSTextField
          label="Filter by Date"
          type="date"
          value={
            dateInput
              ? `${dateInput.year}-${dateInput.month.toString().padStart(2, "0")}-${dateInput.day.toString().padStart(2, "0")}`
              : ""
          }
          onChange={handleChangeDateInput}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
          sx={{ minWidth: 200 }}
          disabled={isFetchingNextPage || isLoading}
        />
        <DSSecondaryButton
          onClick={handleClearDate}
          startIcon={<Clear />}
          disabled={!dateInput}
        >
          Clear
        </DSSecondaryButton>
      </FilterBar>

      {dateInput && (
        <DSBox sx={{ mb: 2 }}>
          <DSChip
            label={`Filtered by: ${dateInput.day}/${dateInput.month}/${dateInput.year}`}
            color="primary"
            variant="outlined"
            onDelete={handleClearDate}
          />
        </DSBox>
      )}

      {data && data.length > 0 ? (
        <DSBox>
          <DSBaseTable
            data={data}
            columns={columns}
            rowBuilder={rowBuilder}
            onPressRow={handleItemClick}
            fetchMore={hasNextPage ? fetchNextPage : undefined}
          />

          {isFetchingNextPage && (
            <DSBox sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <DSCircularProgress size={24} />
            </DSBox>
          )}
        </DSBox>
      ) : (
        <DSBox sx={{ textAlign: "center", py: 4 }}>
          <DSTypography variant="body1" color="text.secondary">
            {dateInput
              ? "No feed history found for the selected date."
              : "No feed history available."}
          </DSTypography>
        </DSBox>
      )}

      {selectedFeedItem && (
        <CalendarEventDialog
          open={modalOpen}
          onClose={handleModalClose}
          calendarEvent={selectedFeedItem}
        />
      )}
    </DSBox>
  );
};

export default FeedHistoryTab;
